// RITA-1504 (Nutzungserfassung Phase 2b) — Schlüsselverzeichnis der Erfassung.
//
// Ein Schlüssel ist entweder der `name` eines Ereignisses (Tabelle, Formular, Suche, Dialog)
// oder der Wert des data-usage-Attributs an einem Bedienelement. Alle Schlüssel stehen hier,
// damit Oberfläche und Auswertung dieselben Namen benutzen und kein freier String in ein
// Ereignis gerät: ein Tippfehler wäre in der Auswertung eine eigene, stille Zeile.
//
// Form: 'bereich.objekt.aktion' — nur Kleinbuchstaben, Ziffern, '.', '-' und '_' (keine
// Umlaute, kein Leerzeichen). Die Namen sind auch über die Arten hinweg eindeutig, damit
// 'berater.entlassen.formular' und 'berater.entlassen.dialog' nicht verwechselbar sind.
// Die vier Marker aus Phase 1b (RITA-1498) bleiben unverändert — e2e/usage-tracking.spec.ts
// hängt an 'kopf.einstellungen'.
import type { PanelId } from '../../pages/AgentEditPage/panelConstants';

/** Erlaubte Schreibweise eines Schlüssels (Prüfung in usageKeys.test.ts). */
export const USAGE_KEY_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;
/** Grenze aus batchSchema.ts (shortName: min 1, max 128). */
export const MAX_USAGE_KEY_LENGTH = 128;

// ---- Art table -------------------------------------------------------------------------
// Ein Name je Tabelle; er ist Pflicht-Prop des Tabellen-Wrappers (components/Table/Table.tsx).
export const TABLE_NAMES = [
  'berater',
  'berater.dateien',
  'berater.leistungen',
  'belege',
  'interessenten',
  'dashboard.startende-berater',
  'dashboard.geburtstage',
  'berichte.leistungen',
  'monatsabschluss.dateien',
  'einstellungen.benutzer',
  'einstellungen.buchungskonten',
  'einstellungen.marken',
  'einstellungen.vertragsarten',
  'einstellungen.laender',
  'einstellungen.levels',
  'einstellungen.raenge',
  'einstellungen.leistungen',
  'einstellungen.steuern',
] as const;

export type UsageTableName = (typeof TABLE_NAMES)[number];

// ---- Art form --------------------------------------------------------------------------
export const FORM_NAMES = [
  'berater.maske',
  'berater.entlassen.formular',
  'beleg.maske',
  'systemdienstleister.maske',
] as const;

export type UsageFormName = (typeof FORM_NAMES)[number];

// ---- Art search ------------------------------------------------------------------------
export const SEARCH_NAMES = ['kopf.suche'] as const;

export type UsageSearchName = (typeof SEARCH_NAMES)[number];

// ---- Art dialog ------------------------------------------------------------------------
// Feste Bestätigungsdialoge. Das Löschen einer Tabellenzeile meldet zusätzlich unter
// '<tabellenname>.loeschen' (aus dem Tabellennamen gebildet, Entwurf E).
export const DIALOG_NAMES = [
  'berater.entlassen.dialog',
  'berater.leistung.hinzufuegen',
  'einstellungen.marken.zeile',
  'mcp.schluessel.erstellen',
  'mcp.schluessel.widerrufen',
  'systemdienstleister.loeschen',
  'systemdienstleister.deaktivieren',
  'systemzugang.aktion',
  'systemzugang.abbrechen',
  // RITA-1526: Dialog „Logo für <Vertragsart>“ unter Einstellungen > Vertragsarten (Speichern/Entfernen
  // bestätigt, Abbrechen verlässt ihn ohne Entscheidung).
  'einstellungen.vertragsarten.logo',
] as const;

export type UsageDialogName = (typeof DIALOG_NAMES)[number];

// ---- Marker (data-usage) ---------------------------------------------------------------
// Nur an Bedienelementen. Der Klick-Zuhörer der Laufzeit nimmt den Wert als Schlüssel und
// speichert für markierte Elemente zusätzlich, dass sie beschriftet sind (clickTarget.ts).
export const MARKERS = {
  anmeldungAnmelden: 'anmeldung.anmelden',
  kopfEinstellungen: 'kopf.einstellungen',
  kopfKonto: 'kopf.konto',
  kopfAbmelden: 'kopf.abmelden',
  kopfSucheOeffnen: 'kopf.suche.oeffnen',
  beraterSpeichern: 'berater.speichern',
  beraterZuruecksetzen: 'berater.zuruecksetzen',
  beraterEntlassen: 'berater.entlassen',
  beraterLeistungNeu: 'berater.leistung.neu',
  belegSpeichern: 'beleg.speichern',
  belegZuruecksetzen: 'beleg.zuruecksetzen',
  monatsabschlussLeistungenZuBelegen: 'monatsabschluss.leistungen-zu-belegen',
  monatsabschlussBelegeZuBuchhaltung: 'monatsabschluss.belege-zu-buchhaltung',
  monatsabschlussMonatSchliessen: 'monatsabschluss.monat-schliessen',
  einstellungenSystemdienstleisterNeu: 'einstellungen.systemdienstleister.neu',
  einstellungenVorlagenNeu: 'einstellungen.vorlagen.neu',
  // Die Marken-Tabelle ersetzt die Bearbeiten-Zelle vollständig; der Knopf steht daher nicht im
  // Kommando-Rahmen des Wrappers und braucht einen festen Marker (Entwurf F).
  einstellungenMarkenBearbeiten: 'einstellungen.marken.bearbeiten',
  // RITA-1505 (Phase 2c): die beiden Bedienelemente der Auswertungsseite Einstellungen > Nutzung.
  // Sie stehen hier und nicht als freier String im JSX, damit der Zeitraumwechsel und der Export
  // in der Auswertung unter einem festen Namen erscheinen (markers.repo.test.ts erzwingt das).
  nutzungExport: 'nutzung.export',
  nutzungZeitraum: 'nutzung.zeitraum',
  // RITA-1522 (Phase 3d): der Schalter des Heatmap-Overlays im Einstellungs-Drawer. Sein Zustand
  // wird NICHT gespeichert (kein Einstellungswert, also auch kein setting-Ereignis); der Klick
  // selbst wird wie an jedem markierten Bedienelement als normaler click erfasst.
  einstellungenHeatmap: 'einstellungen.heatmap',
  // RITA-1526: die Logo-Zelle der Vertragsarten-Tabelle ist ein eigener Knopf außerhalb des
  // Kommando-Rahmens des Wrappers und braucht deshalb einen festen Marker (wie bei den Marken).
  einstellungenVertragsartenLogoOeffnen: 'einstellungen.vertragsarten.logo.oeffnen',
} as const;

/**
 * Aktionsteil der Kommando-Knöpfe einer Tabelle (RITA-1504). Der Tabellen-Wrapper setzt den
 * Marker aus Tabellenname und Aktion zusammen ('einstellungen.benutzer' + 'neu'). Die Wortliste
 * steht hier und nicht im Wrapper, damit die Auswertung eine feste Schreibweise je Aktion
 * vorfindet und ein neuer Kommando-Knopf nicht still einen eigenen Namen erfindet.
 */
export const TABLE_COMMAND_ACTIONS = {
  add: 'neu',
  edit: 'bearbeiten',
  delete: 'loeschen',
  commit: 'speichern',
  cancel: 'abbrechen',
} as const;

export type UsageTableCommandId = keyof typeof TABLE_COMMAND_ACTIONS;

/** Marker eines Kommando-Knopfs: 'einstellungen.benutzer' + 'neu' → 'einstellungen.benutzer.neu'. */
export const tableCommandMarker = (tableName: UsageTableName, command: UsageTableCommandId): string =>
  `${tableName}.${TABLE_COMMAND_ACTIONS[command]}`;

export type UsageMarker = (typeof MARKERS)[keyof typeof MARKERS];

/**
 * Marker der Panel-Umschalter in der Berater-Maske. Der Schlüssel folgt der Panel-Kennung aus
 * pages/AgentEditPage/panelConstants.ts in Marker-Schreibweise ('systemAccess' →
 * 'system-access'); ein neues Panel ohne Marker ist ein Typfehler.
 */
export const PANEL_MARKERS: Record<PanelId, string> = {
  personal: 'berater.panel.personal',
  contract: 'berater.panel.contract',
  finance: 'berater.panel.finance',
  systems: 'berater.panel.systems',
  costs: 'berater.panel.costs',
  systemAccess: 'berater.panel.system-access',
  docs: 'berater.panel.docs',
  notes: 'berater.panel.notes',
  dismiss: 'berater.panel.dismiss',
};
